import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Camera,
  CheckCircle2,
  Eye,
  Timer,
  X,
  type LucideIcon,
} from "lucide-react";
import { AreaChart } from "../components/ui/AreaChart";
import { Badge } from "../components/ui/Badge";
import { BarChart } from "../components/ui/BarChart";
import { BarList } from "../components/ui/BarList";
import { Callout } from "../components/ui/Callout";
import { Card } from "../components/ui/Card";
import { DonutChart } from "../components/ui/DonutChart";
import { Input } from "../components/ui/Input";
import { Pagination } from "../components/ui/Pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import { fmtNumber, fmtPercent, fmtRelative } from "../lib/format";
import type {
  ImageScanLog,
  ImageScanPreview,
  ImageScanStats,
  ImageScanStatus,
} from "../lib/types";

const PAGE_SIZE = 25;
const RANGES: Array<{ label: string; days: number }> = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const STATUS_VARIANT: Record<
  ImageScanStatus,
  "default" | "neutral" | "success" | "error" | "warning"
> = {
  success: "success",
  nothing_detected: "neutral",
  error: "error",
};

type ListResponse = {
  items: ImageScanLog[];
  total: number;
  page: number;
  pageSize: number;
};

export function ScansPage() {
  const [days, setDays] = useState(30);
  const [rawQ, setRawQ] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<ImageScanStatus | "">("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [page, setPage] = useState(1);
  const [preview, setPreview] = useState<ImageScanPreview | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(rawQ.trim()), 250);
    return () => clearTimeout(t);
  }, [rawQ]);

  useEffect(() => {
    setPage(1);
  }, [q, statusFilter, userIdFilter, days]);

  const statsPath = `/v1/admin/image-scans/stats?days=${days}`;
  const stats = useQuery<ImageScanStats>({
    queryKey: ["admin-image-scans-stats", days],
    queryFn: () => api<ImageScanStats>(statsPath),
  });

  const listPath = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (statusFilter) params.set("status", statusFilter);
    if (userIdFilter.trim()) params.set("user_id", userIdFilter.trim());
    if (q) params.set("q", q);
    return `/v1/admin/image-scans?${params.toString()}`;
  }, [page, statusFilter, userIdFilter, q]);

  const list = useQuery<ListResponse>({
    queryKey: ["admin-image-scans", listPath],
    queryFn: () => api<ListResponse>(listPath),
    placeholderData: (prev) => prev,
  });

  const totalPages = list.data
    ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE))
    : 1;
  const from =
    list.data && list.data.items.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to =
    list.data && list.data.items.length > 0
      ? from + list.data.items.length - 1
      : 0;

  async function openPreview(row: ImageScanLog) {
    if (!row.preview_available) return;
    setPreviewError(null);
    setPreviewLoadingId(row.id);
    try {
      const value = await api<ImageScanPreview>(
        `/v1/admin/image-scans/${row.id}/preview`,
      );
      setPreview(value);
    } catch (error) {
      setPreviewError((error as Error).message);
    } finally {
      setPreviewLoadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Image scans
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">
            OpenRouter vision pipeline. Failure rate excludes "nothing_detected"
            — only HTTP / parse / timeout errors count.
          </p>
        </div>
        <RangePicker value={days} onChange={setDays} />
      </header>

      {stats.error && (
        <Callout title="Failed to load stats" variant="error">
          {(stats.error as Error).message}
        </Callout>
      )}

      {stats.isLoading && !stats.data ? (
        <Skeleton rows={4} />
      ) : stats.data ? (
        <ScansDashboard data={stats.data} />
      ) : null}

      <Card>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          Scan log
        </h2>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
          Newest first. Filter by status, user, or food name.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <label className="block text-xs text-gray-500">Status</label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as ImageScanStatus | "")
              }
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-50"
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="nothing_detected">Nothing detected</option>
              <option value="error">Error</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500">User ID</label>
            <Input
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              placeholder="firebase uid"
              autoComplete="off"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500">Food name</label>
            <Input
              type="search"
              value={rawQ}
              onChange={(e) => setRawQ(e.target.value)}
              placeholder="e.g. pizza"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>
      </Card>

      {list.error && (
        <Callout title="Failed to load list" variant="error">
          {(list.error as Error).message}
        </Callout>
      )}

      {previewError && (
        <Callout title="Failed to load preview" variant="error">
          {previewError}
        </Callout>
      )}

      {list.isLoading && !list.data ? (
        <Skeleton rows={5} />
      ) : !list.data || list.data.items.length === 0 ? (
        <Card>
          <p className="text-center text-sm text-gray-500 dark:text-gray-500">
            No scans match the current filters.
          </p>
        </Card>
      ) : (
        <>
          <TableRoot>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>When</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Food</TableHeaderCell>
                  <TableHeaderCell className="text-right">Ing.</TableHeaderCell>
                  <TableHeaderCell className="text-right">
                    Latency
                  </TableHeaderCell>
                  <TableHeaderCell className="text-center">
                    Preview
                  </TableHeaderCell>
                  <TableHeaderCell>User</TableHeaderCell>
                  <TableHeaderCell>Error</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.data.items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-xs text-gray-500">
                      {fmtRelative(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[row.status]}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-900 dark:text-gray-50">
                      {row.food_name ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.ingredient_count}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.latency_ms}ms
                    </TableCell>
                    <TableCell className="text-center">
                      {row.preview_available ? (
                        <button
                          type="button"
                          title="Open debug preview"
                          aria-label="Open debug preview"
                          onClick={() => void openPreview(row)}
                          disabled={previewLoadingId === row.id}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:cursor-wait disabled:opacity-60 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                        >
                          <Eye size={15} strokeWidth={2} />
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="font-mono text-xs text-gray-500"
                      title={row.user_id ?? undefined}
                    >
                      {row.user_id ? row.user_id.slice(0, 8) : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {row.error_code ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableRoot>

          <Pagination
            page={page}
            totalPages={totalPages}
            total={list.data.total}
            from={from}
            to={to}
            busy={list.isFetching}
            onChange={setPage}
          />
        </>
      )}

      {preview && (
        <PreviewDialog preview={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

function PreviewDialog({
  preview,
  onClose,
}: {
  preview: ImageScanPreview;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Debug preview"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl dark:bg-gray-950">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              Debug preview
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Expires {new Date(preview.expires_at).toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-900"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <img
          alt="Scan debug preview"
          src={`data:${preview.mime_type};base64,${preview.image_base64}`}
          className="max-h-[70vh] w-full rounded-md bg-gray-100 object-contain dark:bg-gray-900"
        />
      </div>
    </div>
  );
}

function ScansDashboard({ data }: { data: ImageScanStats }) {
  const { totals, latency } = data;
  const failureTone =
    totals.failure_rate != null && totals.failure_rate > 0.05
      ? "warn"
      : undefined;
  const successTone =
    totals.success_rate != null && totals.success_rate >= 0.85
      ? "ok"
      : undefined;

  const statusMix = [
    { name: "Success", value: totals.success },
    { name: "Nothing detected", value: totals.nothing_detected },
    { name: "Error", value: totals.error },
  ].filter((r) => r.value > 0);
  const topModel = data.by_model[0] ?? null;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <KpiCard
          icon={Camera}
          label="Total scans"
          value={fmtNumber(totals.total)}
          sub={`Window: last ${data.range.days}d`}
        />
        <KpiCard
          icon={CheckCircle2}
          label="Success rate"
          value={
            totals.success_rate != null ? fmtPercent(totals.success_rate) : "—"
          }
          sub={`${fmtNumber(totals.success)} of ${fmtNumber(totals.total)}`}
          tone={successTone}
        />
        <KpiCard
          icon={AlertTriangle}
          label="Failure rate"
          value={
            totals.failure_rate != null ? fmtPercent(totals.failure_rate) : "—"
          }
          sub={`${fmtNumber(totals.error)} errors`}
          tone={failureTone}
        />
        <KpiCard
          icon={Timer}
          label="p95 latency"
          value={latency.p95_ms != null ? `${latency.p95_ms}ms` : "—"}
          sub={
            latency.p50_ms != null && latency.avg_ms != null
              ? `p50 ${latency.p50_ms}ms · avg ${latency.avg_ms}ms`
              : undefined
          }
        />
        <KpiCard
          icon={Activity}
          label="Vision source"
          value={topModel ? "OpenRouter" : "—"}
          sub={
            topModel
              ? `${topModel.model} · ${fmtNumber(topModel.total)} calls`
              : "No model calls yet"
          }
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:items-stretch">
        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Daily breakdown
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Scan volume by status, bucketed UTC.
          </p>
          {data.by_day.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No scans in this window.
            </p>
          ) : (
            <AreaChart
              data={data.by_day.map((d) => ({
                day: d.day,
                Success: d.success,
                "Nothing detected": d.nothing_detected,
                Error: d.error,
              }))}
              index="day"
              categories={["Success", "Nothing detected", "Error"]}
              colors={["emerald", "gray", "pink"]}
              valueFormatter={fmtNumber}
              showLegend
              className="mt-6 h-64"
            />
          )}
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Status mix
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            All scans in window.
          </p>
          {statusMix.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No scans yet.
            </p>
          ) : (
            <DonutChart
              data={statusMix}
              category="name"
              value="value"
              colors={["emerald", "gray", "pink"]}
              valueFormatter={fmtNumber}
              label={fmtNumber(totals.total)}
              showLabel
              className="mx-auto mt-6 h-44 w-44"
            />
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Top error codes
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            Most common failure reasons.
          </p>
          {data.top_error_codes.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No errors recorded.
            </p>
          ) : (
            <BarList
              data={data.top_error_codes.map((e) => ({
                name: e.error_code,
                value: e.count,
              }))}
              valueFormatter={fmtNumber}
              className="mt-6"
            />
          )}
        </Card>

        <Card className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Top recognised foods
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            From successful scans.
          </p>
          {data.top_foods.length === 0 ? (
            <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-500">
              No successful scans yet.
            </p>
          ) : (
            <BarChart
              data={data.top_foods.map((f) => ({
                name: f.food_name,
                Count: f.count,
              }))}
              index="name"
              categories={["Count"]}
              colors={["cyan"]}
              valueFormatter={fmtNumber}
              yAxisWidth={56}
              showLegend={false}
              className="mt-6 h-64"
            />
          )}
        </Card>
      </div>

      {data.by_user.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            Top users by scan volume
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            uid truncated; full value on row hover.
          </p>
          <ul className="mt-4 divide-y divide-stone-200 dark:divide-white/10">
            {data.by_user.map((u) => (
              <li
                key={u.user_id}
                className="flex items-center justify-between gap-4 py-2.5 text-sm"
                title={u.user_id}
              >
                <span className="font-mono text-xs text-gray-500">
                  {u.user_id.slice(0, 12)}
                </span>
                <div className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                  <span className="tabular-nums">
                    {fmtNumber(u.total)} total
                  </span>
                  <Badge variant="success">{u.success} ok</Badge>
                  {u.error > 0 && <Badge variant="error">{u.error} err</Badge>}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

function RangePicker({
  value,
  onChange,
}: {
  value: number;
  onChange: (days: number) => void;
}) {
  return (
    <div
      className="inline-flex rounded-md border border-gray-200 bg-white p-0.5 text-xs dark:border-gray-800 dark:bg-gray-950"
      role="tablist"
      aria-label="Date range"
    >
      {RANGES.map((r) => (
        <button
          key={r.days}
          type="button"
          role="tab"
          aria-selected={r.days === value}
          onClick={() => onChange(r.days)}
          className={
            r.days === value
              ? "rounded-md bg-gray-900 px-3 py-1 text-white dark:bg-gray-100 dark:text-gray-900"
              : "rounded-md px-3 py-1 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-900"
          }
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sub?: string;
  tone?: "ok" | "warn";
}) {
  const valueColor =
    tone === "ok"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : "text-gray-900 dark:text-gray-50";
  return (
    <Card className="min-h-[112px]">
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Icon size={14} strokeWidth={2} />
        {label}
      </div>
      <p className={`mt-2 text-2xl font-semibold tabular-nums ${valueColor}`}>
        {value}
      </p>
      {sub && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{sub}</p>
      )}
    </Card>
  );
}

function Skeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-16 w-full animate-pulse rounded-md bg-gray-100 dark:bg-gray-900"
        />
      ))}
    </div>
  );
}
